import React from "react";

import { useTranslation } from "react-i18next";

import { useOutletContext, useLocation } from "react-router-dom";

import "./LenderDashboard.css";

import "./LenderDashboardSections.css";

import SummaryCard from "./SummaryCard";

import LoanCasesPanel from "./LoanCasesPanel";

import MessagesPanel from "./MessagesPanel";

export default function LenderDashboard() {
  const { t } = useTranslation("lender-dashboard");

  const location = useLocation();

  const { me, dashboardSummary, isLoadingMe, refreshMe } = useOutletContext();

  // ========================================================
  // REFRESH LENDER DATA WHEN DASHBOARD OPENS
  // ========================================================

  // ========================================================
  // REFRESH LENDER DATA ON EVERY DASHBOARD ENTRY
  // ========================================================

  const refreshMeRef = React.useRef(refreshMe);

  React.useEffect(() => {
    refreshMeRef.current = refreshMe;
  }, [refreshMe]);

  React.useEffect(() => {
    let active = true;

    async function refreshDashboard() {
      try {
        await refreshMeRef.current?.();
      } catch (error) {
        if (!active) {
          return;
        }
      }
    }

    refreshDashboard();

    return () => {
      active = false;
    };
  }, [location.key]);

  // ========================================================
  // LOAN CASES
  // ========================================================

  const loanCases = me?.loan_cases_requiring_action ?? [];

  const loanCasesTotal =
    me?.loan_cases_requiring_action_total ?? loanCases.length;

  const loanCasesHasMore = me?.loan_cases_requiring_action_has_more ?? false;

  // ========================================================
  // MESSAGES
  // ========================================================

  const messages = me?.latest_messages ?? [];

  const messagesTotal = me?.latest_messages_total ?? messages.length;

  const messagesHasMore = me?.latest_messages_has_more ?? false;

  const unreadMessagesCount =
    me?.unread_messages_count ?? dashboardSummary?.unread_messages_count ?? 0;

  return (
    <div className="lender-dashboard">
      <header className="lender-dashboard__hero lender-dashboard__fade-1">
        <div className="lender-dashboard__hero-copy">
          <h1 className="lender-dashboard__title">{t("overview.title")}</h1>

          <p className="lender-dashboard__subtitle">{t("overview.subtitle")}</p>
        </div>
      </header>

      <section className="lender-dashboard__summary-grid">
        <SummaryCard
          type="linkedCases"
          count={dashboardSummary?.linked_loan_cases_count ?? 0}
          label={dashboardSummary?.linked_loan_cases_label}
          helperCount={dashboardSummary?.linked_loan_cases_this_week ?? 0}
          helperType="week"
          isLoading={isLoadingMe}
          delayClass="lender-dashboard__fade-1"
        />

        <SummaryCard
          type="manualCases"
          count={dashboardSummary?.manual_loan_cases_count ?? 0}
          label={dashboardSummary?.manual_loan_cases_label}
          helperCount={dashboardSummary?.manual_loan_cases_this_week ?? 0}
          helperType="week"
          isLoading={isLoadingMe}
          delayClass="lender-dashboard__fade-2"
        />

        <SummaryCard
          type="messages"
          count={unreadMessagesCount}
          label={
            dashboardSummary?.messages_label || t("summary.messages.subtitle")
          }
          helperCount={messagesTotal}
          helperType="total"
          isLoading={isLoadingMe}
          delayClass="lender-dashboard__fade-3"
        />
      </section>

      {/* ====================================================
          MAIN CONTENT
          ==================================================== */}

      <section className="lender-dashboard__main-grid">
        <LoanCasesPanel
          items={loanCases}
          totalCount={loanCasesTotal}
          hasMore={loanCasesHasMore}
          isLoading={isLoadingMe}
        />

        <MessagesPanel
          items={messages}
          totalCount={messagesTotal}
          hasMore={messagesHasMore}
          isLoading={isLoadingMe}
        />
      </section>
    </div>
  );
}
